import React, { useState } from "react";
import "./DeleteAccount.css";

const DeleteAccount = () => {
  const [otpSent, setOtpSent] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    countryCode: "+254",
    phoneNumber: "",
    reason: "",
    deleteConfirmation: false,
  });
  const [otp, setOtp] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const countryCodeOptions = [{ code: "+254", country: "Kenya" }];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Mobile number is required";
    } else if (!/^\d{9}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid 9-digit phone number";
    }

    if (!formData.reason.trim()) {
      newErrors.reason = "Please tell us why you want to delete your account";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    if (!otpSent) {
      if (!validateForm()) {
        return;
      }

      setLoading(true);

      try {
        const fullPhoneNumber = formData.countryCode + formData.phoneNumber;
        const response = await fetch(
          "https://sunking.glpapps.com/sunking-app/api/delete-data/send-otp/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              phoneNumber: fullPhoneNumber,
            }),
          },
        );

        const data = await response.json();

        if (response.ok) {
          setOtpSent(true);
        } else {
          setApiError(data.message || "Failed to send OTP. Please try again.");
        }
      } catch (error) {
        setApiError(
          "Network error. Please check your connection and try again.",
        );
      } finally {
        setLoading(false);
      }
    } else {
      // Second step: Confirm deletion with OTP
      if (!otp.trim()) {
        setErrors({ otp: "Please enter the OTP" });
        return;
      }

      if (!formData.deleteConfirmation) {
        setErrors({
          deleteConfirmation:
            "Please confirm that your account deletion cannot be undone",
        });
        return;
      }

      setLoading(true);

      try {
        const fullPhoneNumber = formData.countryCode + formData.phoneNumber;
        const response = await fetch(
          "https://sunking.glpapps.com/sunking-app/api/delete-data/erase-account-by-web/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              phoneNumber: fullPhoneNumber,
              otp: otp,
              name: formData.name,
              reason: formData.reason,
            }),
          },
        );

        const data = await response.json();

        if (response.ok) {
          setIsDeleted(true);
        } else {
          setApiError(
            data.message || "Invalid OTP or deletion failed. Please try again.",
          );
        }
      } catch (error) {
        setApiError(
          "Network error. Please check your connection and try again.",
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleOtpChange = (e) => {
    setOtp(e.target.value);
    if (errors.otp) {
      setErrors((prev) => ({
        ...prev,
        otp: "",
      }));
    }
  };

  return (
    <div className="delete-account">
      <div className="delete-container">
        <div className="delete-card">
          <div className="delete-content">
            <h1 className="delete-title">Delete Account</h1>
            <p className="delete-description">
              You can request deletion of you Sun King App account and all
              associated data using the form below.
            </p>
          </div>

          {!isDeleted && (
            <form onSubmit={handleSubmit} className="delete-form">
              <div className={`form-group ${otpSent ? "readonly" : ""}`}>
                <label>Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={`${errors.name ? "error" : ""} ${
                    otpSent ? "readonly-input" : ""
                  }`}
                  readOnly={otpSent}
                />
                {errors.name && (
                  <span className="error-message">{errors.name}</span>
                )}
              </div>

              <div className={`form-group ${otpSent ? "readonly" : ""}`}>
                <label>Mobile Number</label>
                <div className="phone-input-group">
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleInputChange}
                    className={`country-code-select ${
                      otpSent ? "readonly-input" : ""
                    }`}
                    disabled={otpSent}
                  >
                    {countryCodeOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.code} ({option.country})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="1234567890"
                    className={`${errors.phoneNumber ? "error" : ""} ${
                      otpSent ? "readonly-input" : ""
                    }`}
                    readOnly={otpSent}
                  />
                </div>
                {errors.phoneNumber && (
                  <span className="error-message">{errors.phoneNumber}</span>
                )}
              </div>

              <div className={`form-group ${otpSent ? "readonly" : ""}`}>
                <label>Reason for Deletion</label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  placeholder="Please tell us why you want to delete your account"
                  rows="4"
                  className={`${errors.reason ? "error" : ""} ${
                    otpSent ? "readonly-input" : ""
                  }`}
                  readOnly={otpSent}
                />
                {errors.reason && (
                  <span className="error-message">{errors.reason}</span>
                )}
              </div>

              {otpSent && (
                <>
                  <div className="form-group">
                    <label>Enter OTP</label>
                    <input
                      type="number"
                      value={otp}
                      onChange={handleOtpChange}
                      placeholder="Enter OTP"
                      className={errors.otp ? "error" : ""}
                      maxLength="6"
                    />
                    {errors.otp && (
                      <span className="error-message">{errors.otp}</span>
                    )}
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        id="delete-confirmation"
                        name="deleteConfirmation"
                        checked={formData.deleteConfirmation}
                        onChange={handleInputChange}
                        className="checkbox-input"
                      />
                      <span className="checkbox-text">
                        I confirm that I want to permanently delete my Sun King
                        App account and understand that this action cannot be
                        undone.
                      </span>
                    </label>
                    {errors.deleteConfirmation && (
                      <span className="error-message">
                        {errors.deleteConfirmation}
                      </span>
                    )}
                  </div>
                </>
              )}

              {apiError && <div className="api-error-message">{apiError}</div>}

              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading
                  ? otpSent
                    ? "Confirming..."
                    : "Sending..."
                  : otpSent
                  ? "Confirm Deletion"
                  : "Send OTP"}
              </button>
            </form>
          )}

          {isDeleted && (
            <div className="success-message-container">
              <div className="success-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" fill="#FFC107" />
                  <path
                    d="M8 12L11 15L16 9"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="success-title">Request Received</h2>
              <p className="success-text">
                Your account deletion request has been received and will be
                processed within <strong>2-3 working days</strong>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
